import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Button,
  alpha,
  Chip,
  LinearProgress,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux'; // LAP-733 (GE022) round 10 fix-up
import { useSubscription } from './SubscriptionContext';
import { goToButtonTarget } from './publicationHandlers';
import { getTranslation, getUserLanguage } from './translations'; // LAP-733 (GE022) round 10 fix-up

// LAP-733 (GE022) round 10 fix-up — map server-side publication.type to i18n key
const PUBLICATION_TYPE_KEY = {
  Insight: 'publication',
  Publication: 'publication',
  Summary: 'summary',
  Reminder: 'reminder',
  Actionable: 'actionable',
  News: 'news',
};

const ReminderCard = ({ publication, onInteraction, showArchivedIndicators = false }) => {
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [urgencyLevel, setUrgencyLevel] = useState('normal'); // normal, warning, urgent

  // LAP-733 (GE022) round 10 fix-up — resolve user language + BCP-47 locale
  const siteUser = useSelector((store) => store.user);
  const lang = getUserLanguage(siteUser);
  const dateLocale = ({ fr: 'fr-FR', en: 'en-GB', de: 'de-DE', it: 'it-IT', es: 'es-ES' })[lang] || lang || 'fr-FR';

  // LAP-733 (GE022) round 10 fix-up — translate the publication.type badge
  const displayType = getTranslation(PUBLICATION_TYPE_KEY[publication.type] || 'publication', lang);

  // Calculate time remaining if deadline/expiry date is provided
  useEffect(() => {
    if (publication.deadline || publication.expiry_date) {
      const deadline = new Date(publication.deadline || publication.expiry_date);
      const now = new Date();
      const diff = deadline - now;
      
      if (diff < 0) {
        setUrgencyLevel('urgent');
        setTimeRemaining('Overdue');
      } else {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        
        if (days === 0) {
          setUrgencyLevel('urgent');
          setTimeRemaining(`${hours}h remaining`);
        } else if (days <= 3) {
          setUrgencyLevel('warning');
          setTimeRemaining(`${days}d ${hours}h remaining`);
        } else {
          setUrgencyLevel('normal');
          setTimeRemaining(`${days} days remaining`);
        }
      }
    }
  }, [publication.deadline, publication.expiry_date]);

  const handleDismiss = (e) => {
    e.stopPropagation();
    onInteraction(publication.id, 'dismiss');
  };

  const handleLike = (e) => {
    e.stopPropagation();
    onInteraction(publication.id, 'like');
  };

  const handleSave = (e) => {
    e.stopPropagation();
    onInteraction(publication.id, 'save');
  };

  const handleAction = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    goToButtonTarget(publication.button_target, navigate, publication);
    onInteraction(publication.id, 'click');
  };

  const hasButton = (publication.button_text || publication.button_target) && !!publication.button_target;

  const reminderColor = '#ef4444'; // Red
  const reminderGradient = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
  const isCompleted = publication.status === 'completed' || 
                      publication.status === 'completed_active' || 
                      publication.status === 'completed_archived';
  
  const getUrgencyColor = () => {
    switch (urgencyLevel) {
      case 'urgent':
        return '#ef4444';
      case 'warning':
        return '#f59e0b';
      default:
        return '#3b82f6';
    }
  };

  return (
    <Card
      onClick={handleAction}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#1a1d2e',
        borderRadius: '16px',
        border: `1px solid ${isCompleted ? alpha('#10b981', 0.5) : alpha(reminderColor, 0.3)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        cursor: publication.button_target ? 'pointer' : 'default',
        opacity: isCompleted ? 0.7 : 1,
        '&:hover': {
          transform: isCompleted ? 'none' : 'translateY(-4px)',
          boxShadow: isCompleted ? 'none' : `0 20px 25px -5px ${alpha(reminderColor, 0.4)}, 0 10px 10px -5px ${alpha(
            reminderColor,
            0.3,
          )}`,
          borderColor: isCompleted ? alpha('#10b981', 0.7) : alpha(reminderColor, 0.5),
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: isCompleted ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : reminderGradient,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="start" gap={1} flexGrow={1}>
            <Typography
              variant="h4"
              component="span"
              sx={{
                fontSize: '2rem',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              }}
            >
              {publication.icon || '⏰'}
            </Typography>

            <Box flexGrow={1}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    lineHeight: 1.4,
                    color: '#E0E0E0',
                    textDecoration: isCompleted ? 'line-through' : 'none',
                    opacity: isCompleted ? 0.6 : 1,
                  }}
                >
                  {publication.title}
                </Typography>
                {isCompleted && (
                  <TaskAltIcon sx={{ color: '#10b981', fontSize: '1.2rem' }} />
                )}
                {!isCompleted && urgencyLevel === 'urgent' && (
                  <WarningIcon sx={{ color: '#ef4444', fontSize: '1.2rem' }} />
                )}
              </Box>

              {publication.subtitle && (
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha('#E0E0E0', 0.7),
                    fontSize: '0.85rem',
                    mb: 1,
                  }}
                >
                  {publication.subtitle}
                </Typography>
              )}

              {/* Time Remaining Indicator */}
              {timeRemaining && (
                <Chip
                  label={timeRemaining}
                  size="small"
                  sx={{
                    bgcolor: alpha(getUrgencyColor(), 0.2),
                    color: getUrgencyColor(),
                    fontSize: '0.75rem',
                    height: '22px',
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>
          </Box>
          
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                color: '#ef4444',
                bgcolor: 'rgba(239, 68, 68, 0.1)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Image */}
        {publication.image_url && (
          <Box
            sx={{
              width: '100%',
              borderRadius: '12px',
              overflow: 'hidden',
              mb: 2,
              mt: 1,
            }}
          >
            <img
              src={publication.image_url}
              alt={publication.title}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                objectFit: 'cover',
                maxHeight: '300px',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>
        )}

        {/* Text Content */}
        {publication.text1 && (
          <Typography
            variant="body2"
            sx={{
              color: alpha('#E0E0E0', 0.9),
              fontSize: '0.9rem',
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            {publication.text1}
          </Typography>
        )}

        {/* Link button when button_text or button_target is defined */}
        {hasButton && (
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAction(e);
            }}
            fullWidth
            sx={{
              mb: 2,
              borderColor: alpha(reminderColor, 0.5),
              color: reminderColor,
              textTransform: 'none',
              '&:hover': {
                borderColor: reminderColor,
                bgcolor: alpha(reminderColor, 0.1),
              },
            }}
          >
            {publication.button_text || 'Voir'}
          </Button>
        )}
        
        {/* Completed Badge */}
        {isCompleted && (
          <Box 
            display="flex" 
            alignItems="center" 
            gap={1} 
            mb={2}
            sx={{
              bgcolor: alpha('#10b981', 0.15),
              border: `1px solid ${alpha('#10b981', 0.3)}`,
              borderRadius: '8px',
              p: 1.5,
            }}
          >
            <TaskAltIcon sx={{ color: '#10b981', fontSize: '1.5rem' }} />
            <Typography
              variant="body2"
              sx={{
                color: '#10b981',
                fontWeight: 600,
                fontSize: '0.9rem',
              }}
            >
              Completed
            </Typography>
          </Box>
        )}

        {/* Interaction buttons */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mt="auto">
          <Box display="flex" gap={1}>
            <IconButton
              size="small"
              onClick={handleLike}
              sx={{
                color: publication.was_liked ? '#ef4444' : 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                },
              }}
            >
              {publication.was_liked ? (
                <FavoriteIcon fontSize="small" />
              ) : (
                <FavoriteBorderIcon fontSize="small" />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={handleSave}
              sx={{
                color: publication.is_saved ? '#f59e0b' : 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(245, 158, 11, 0.1)',
                },
              }}
            >
              {publication.is_saved ? (
                <BookmarkIcon fontSize="small" />
              ) : (
                <BookmarkBorderIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            {/* Type Chip */}
            <Chip
              label={displayType}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: '20px',
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
            {/* Show saved/liked indicators in archived view */}
            {showArchivedIndicators && (
              <>
                {publication.is_saved && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: alpha('#8b5cf6', 0.2),
                      color: '#8b5cf6',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: '4px',
                      fontSize: '0.65rem',
                    }}
                  >
                    <BookmarkIcon sx={{ fontSize: '0.7rem' }} />
                    Saved
                  </Box>
                )}
                {publication.was_liked && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: alpha('#ef4444', 0.2),
                      color: '#ef4444',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: '4px',
                      fontSize: '0.65rem',
                    }}
                  >
                    <FavoriteIcon sx={{ fontSize: '0.7rem' }} />
                    Liked
                  </Box>
                )}
              </>
            )}
            {publication.datetime_displayed && (
              <Typography
                variant="caption"
                sx={{
                  color: alpha('#E0E0E0', 0.5),
                  fontSize: '0.75rem',
                }}
              >
                {new Date(publication.datetime_displayed).toLocaleDateString(dateLocale) /* LAP-733 (GE022) round 10 fix-up */}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReminderCard;

